"use client";

import { BrandPageQueryObj } from "@faire/retailer-shared/search/BrandPageQueryObj";
import { IProductCollectionMetadataResponse } from "@faire/web-api--source/indigofair/data/IProductCollectionMetadataResponse";
import { ISearchBrandLeanProductTilesResponse } from "@faire/web-api--source/indigofair/data/ISearchBrandLeanProductTilesResponse";
import { ISearchBrandProductsFiltersResponse } from "@faire/web-api--source/indigofair/data/ISearchBrandProductsFiltersResponse";
import React, { ReactNode, useContext } from "react";

import { IProductsData } from "@faire/retailer/views/brand/contexts/utils/productsDataAggregator";

import { useBrandProductsQueryWithQueryParams } from "./__internal__/useBrandProductsQuery";

interface IBrandPageProductsContext {
  productsQueryPromise?: Promise<
    ISearchBrandLeanProductTilesResponse | undefined
  >;
  productsFiltersPromise?: Promise<
    ISearchBrandProductsFiltersResponse | undefined
  >;
  productsCollectionsPromise?: Promise<
    IProductCollectionMetadataResponse | undefined
  >;
  legacyProductsQueryData?: IProductsData | undefined;
  brandToken: string | undefined;
  queryParams: BrandPageQueryObj | undefined;
}

const DEFAULT_BRAND_PRODUCTS_STATE_CONTEXT: IBrandPageProductsContext = {
  productsQueryPromise: Promise.resolve(undefined),
  productsFiltersPromise: Promise.resolve(undefined),
  productsCollectionsPromise: Promise.resolve(undefined),
  legacyProductsQueryData: undefined,
  brandToken: "",
  queryParams: undefined,
};

export const BrandPageProductsStateContext =
  React.createContext<IBrandPageProductsContext>(
    DEFAULT_BRAND_PRODUCTS_STATE_CONTEXT
  );

export const useBrandPageProductsStateContext = () => {
  const value = useContext(BrandPageProductsStateContext);

  if (value === DEFAULT_BRAND_PRODUCTS_STATE_CONTEXT) {
    throw new Error(
      "useBrandPageProductsStateContext must be used within a BrandPageProductsStateContext.Provider"
    );
  }
  return value;
};

export const BrandPageProductsClientStateContextProvider: React.FC<{
  brandToken: string;
  children: ReactNode;
}> = ({ brandToken, children }) => {
  const { productsQueryData, queryParams } =
    useBrandProductsQueryWithQueryParams(brandToken);
  return (
    <BrandPageProductsStateContext.Provider
      value={{
        legacyProductsQueryData: productsQueryData,
        brandToken,
        queryParams,
      }}
    >
      {children}
    </BrandPageProductsStateContext.Provider>
  );
};
